import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getLabData } from '../services/labService';
import { getMemberLabData } from '../services/memberLabService';
import { createShareTokenMFUUser, getNotifications } from '../services/mfuUserService';
import MainHeader from '../Components/MainHeader';
import PDFViewerModal from '../Components/PDFViewerModal';

const TestResultsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e1e5e9;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
`;

const Tab = styled.div`
  padding: 12px 5px;
  text-align: center;
  font-size: 13px;
  font-weight: bold;
  color: ${props => props.active ? 'white' : '#B0B0B0'};
  background-color: ${props => props.active ? '#00BBD3' : '#F2F2F2'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  line-height: 1.2;
  white-space: nowrap;

  ${props => {
    if (props.children === 'สุขภาพของคุณ') {
      return `width: 152px;`;
    } else if (props.children === 'ผลตรวจรวม') {
      return `width: 120px;`;
    } else if (props.children === 'ผลตรวจเปรียบเทียบ') {
      return `width: 168px;`;
    }
    return '';
  }}

  &:hover {
    background-color: ${props => props.active ? '#00BBD3' : '#e9ecef'};
  }
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const ResultCard = styled.div`
  background-color: white;
  border-radius: 5px 5px 0 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const CardHeader = styled.div`
  background-color: #00BBD3;
  padding: 16px 20px;
  color: white;
  text-align: center;
`;

const HeaderText = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333333;
  margin: 20px 0 12px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CardContainer = styled.div`
  margin-bottom: 24px;
`;

const DownloadContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const DownloadButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 25px;
  font-weight: 700;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  padding: 8px 12px;

  &:hover {
    color: #00BBD3;
  }
`;

const PDFIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  padding-left: ${props => props.indent ? '24px' : '0'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;
  text-align: right;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e1e5e9;
  margin: 16px 0;
`;

// =====================================================
// FIELD_MAPPING - Map จาก API field name ไปเป็น display
// ครอบคลุมทุก parameter ที่ API อาจส่งมา
// =====================================================
const FIELD_MAPPING = {
  // ===== ความดันโลหิต / ชีพจร / น้ำหนัก =====
  'sys/dias': { label: 'ความดันโลหิต', unit: 'มม.ปรอท', category: 'vital', order: 1 },
  'systolic': { label: 'ความดันโลหิตซิสโตลิก', unit: 'มม.ปรอท', category: 'vital', order: 1 },
  'diastolic': { label: 'ความดันโลหิตไดแอสโตลิก', unit: 'มม.ปรอท', category: 'vital', order: 1 },
  'pulse': { label: 'ชีพจร', unit: 'ครั้ง/นาที', category: 'vital', order: 2 },
  'respiratoryrate': { label: 'อัตราการหายใจ', unit: 'ครั้ง/นาที', category: 'vital', order: 3 },
  'respiratory_rate': { label: 'อัตราการหายใจ', unit: 'ครั้ง/นาที', category: 'vital', order: 3 },
  'weight': { label: 'น้ำหนัก', unit: 'กก.', category: 'vital', order: 4 },
  'bodyweight': { label: 'น้ำหนัก', unit: 'กก.', category: 'vital', order: 4 },
  'body weight': { label: 'น้ำหนัก', unit: 'กก.', category: 'vital', order: 4 },
  'height': { label: 'ส่วนสูง', unit: 'ซม.', category: 'vital', order: 5 },
  'bmi': { label: 'BMI', unit: 'กก./ม.²', category: 'vital', order: 6 },

  // ===== ความสมบูรณ์ของเม็ดเลือด (CBC) =====
  'wbc': { label: 'เม็ดเลือดขาว (WBC)', unit: 'cells/µL', category: 'cbc', order: 1 },
  'rbc': { label: 'เม็ดเลือดแดง (RBC)', unit: 'cells/µL', category: 'cbc', order: 2 },
  'hemoglobin': { label: 'ฮีโมโกลบิน (Hb)', unit: 'g/dL', category: 'cbc', order: 3 },
  'hb': { label: 'ฮีโมโกลบิน (Hb)', unit: 'g/dL', category: 'cbc', order: 3 },
  'hematocrit': { label: 'ฮีมาโตคริต (Hct)', unit: '%', category: 'cbc', order: 4 },
  'hct': { label: 'ฮีมาโตคริต (Hct)', unit: '%', category: 'cbc', order: 4 },
  'platelet': { label: 'เกล็ดเลือด (Platelet)', unit: 'cells/µL', category: 'cbc', order: 5 },
  'platelets': { label: 'เกล็ดเลือด (Platelet)', unit: 'cells/µL', category: 'cbc', order: 5 },
  'plt': { label: 'เกล็ดเลือด (Platelet)', unit: 'cells/µL', category: 'cbc', order: 5 },
  'neutrophile': { label: 'นิวโทรฟิล (Neutrophil)', unit: '%', category: 'cbc', order: 6 },
  'lymphocyte': { label: 'ลิมโฟไซต์ (Lymphocyte)', unit: '%', category: 'cbc', order: 7 },
  'lymphocyte-count': { label: 'ลิมโฟไซต์ (Lymphocyte)', unit: '%', category: 'cbc', order: 7 },
  'monocyte': { label: 'โมโนไซต์ (Monocyte)', unit: '%', category: 'cbc', order: 8 },
  'monocyte-count': { label: 'โมโนไซต์ (Monocyte)', unit: '%', category: 'cbc', order: 8 },
  'basophil': { label: 'เบโซฟิล (Basophil)', unit: '%', category: 'cbc', order: 9 },
  'basophil-count': { label: 'เบโซฟิล (Basophil)', unit: '%', category: 'cbc', order: 9 },
  'eosinophil': { label: 'อีโอซิโนฟิล (Eosinophil)', unit: '%', category: 'cbc', order: 10 },
  'eosinophile': { label: 'อีโอซิโนฟิล (Eosinophil)', unit: '%', category: 'cbc', order: 10 },
  'cbceosinophileresult': { label: 'อีโอซิโนฟิล (Eosinophil)', unit: '%', category: 'cbc', order: 10 },
  'eosinophil_percent': { label: 'อีโอซิโนฟิล (Eosinophil)', unit: '%', category: 'cbc', order: 10 },
  'eosinophile_percent': { label: 'อีโอซิโนฟิล (Eosinophil)', unit: '%', category: 'cbc', order: 10 },
  'eosinophil-count': { label: 'อีโอซิโนฟิล (Eosinophil)', unit: '%', category: 'cbc', order: 10 },
  'eosinophil_count': { label: 'อีโอซิโนฟิล (Eosinophil)', unit: '%', category: 'cbc', order: 10 },
  'mcv': { label: 'MCV', unit: 'fL', category: 'cbc', order: 11 },
  'mch': { label: 'MCH', unit: 'pg/cell', category: 'cbc', order: 12 },
  'mchc': { label: 'MCHC', unit: 'g/dL', category: 'cbc', order: 13 },
  'rdw': { label: 'RDW', unit: '%', category: 'cbc', order: 14 },
  'mpv': { label: 'MPV', unit: 'fL', category: 'cbc', order: 15 },
  'rbc_morphology': { label: 'RBC Morphology', unit: '', category: 'cbc', order: 16 },
  'rbcmorphology': { label: 'RBC Morphology', unit: '', category: 'cbc', order: 16 },
  'rbcmorph': { label: 'RBC Morphology', unit: '', category: 'cbc', order: 16 },
  'rbcmorphologyresult': { label: 'RBC Morphology', unit: '', category: 'cbc', order: 16 },

  // ===== น้ำตาลในเลือด =====
  'fbs/fpg': { label: 'Fasting blood glucose', unit: 'mg/dL', category: 'sugar', order: 1 },
  'sugar': { label: 'Fasting blood glucose', unit: 'mg/dL', category: 'sugar', order: 1 },
  'fbs': { label: 'Fasting blood glucose', unit: 'mg/dL', category: 'sugar', order: 1 },
  'fpg': { label: 'Fasting blood glucose', unit: 'mg/dL', category: 'sugar', order: 1 },
  'glucose': { label: 'Fasting blood glucose', unit: 'mg/dL', category: 'sugar', order: 1 },
  'non_npo_glucose': { label: 'Non-NPO blood glucose', unit: 'mg/dL', category: 'sugar', order: 2 },
  'nonnpoglucose': { label: 'Non-NPO blood glucose', unit: 'mg/dL', category: 'sugar', order: 2 },
  'nonnpo': { label: 'Non-NPO blood glucose', unit: 'mg/dL', category: 'sugar', order: 2 },
  'rbs': { label: 'Non-NPO blood glucose', unit: 'mg/dL', category: 'sugar', order: 2 },
  'hba1c': { label: 'HbA1C', unit: '%', category: 'sugar', order: 3 },

  // ===== ไขมันในเลือด =====
  'cholesterol': { label: 'คอเลสเตอรอลรวม', unit: 'mg/dL', category: 'lipid', order: 1 },
  'chol': { label: 'คอเลสเตอรอลรวม', unit: 'mg/dL', category: 'lipid', order: 1 },
  'total_cholesterol': { label: 'คอเลสเตอรอลรวม', unit: 'mg/dL', category: 'lipid', order: 1 },
  'triglyceride': { label: 'ไตรกลีเซอไรด์', unit: 'mg/dL', category: 'lipid', order: 2 },
  'tg': { label: 'ไตรกลีเซอไรด์', unit: 'mg/dL', category: 'lipid', order: 2 },
  'hdl': { label: 'HDL (ไขมันดี)', unit: 'mg/dL', category: 'lipid', order: 3 },
  'ldl': { label: 'LDL (ไขมันไม่ดี)', unit: 'mg/dL', category: 'lipid', order: 4 },
  'vldl': { label: 'VLDL', unit: 'mg/dL', category: 'lipid', order: 5 },

  // ===== การทำงานของไต =====
  'creatinine': { label: 'Creatinine', unit: 'mg/dL', category: 'kidney', order: 1 },
  'bun': { label: 'BUN', unit: 'mg/dL', category: 'kidney', order: 2 },
  'egfr': { label: 'eGFR', unit: 'mL/min/1.73m', category: 'kidney', order: 3 },
  'urea': { label: 'Urea', unit: 'mg/dL', category: 'kidney', order: 4 },

  // ===== การทำงานของตับ =====
  'sgot': { label: 'SGOT (AST)', unit: 'U/L', category: 'liver', order: 1 },
  'ast': { label: 'AST (SGOT)', unit: 'U/L', category: 'liver', order: 1 },
  'sgpt': { label: 'SGPT (ALT)', unit: 'U/L', category: 'liver', order: 2 },
  'alt': { label: 'ALT (SGPT)', unit: 'U/L', category: 'liver', order: 2 },
  'alp': { label: 'Alkaline Phosphatase', unit: 'U/L', category: 'liver', order: 3 },
  'alkaline_phosphatase': { label: 'Alkaline Phosphatase', unit: 'U/L', category: 'liver', order: 3 },
  'albumin': { label: 'Albumin', unit: 'g/dL', category: 'liver', order: 4 },
  'globulin': { label: 'Globulin', unit: 'g/dL', category: 'liver', order: 5 },
  'total_protein': { label: 'Total Protein', unit: 'g/dL', category: 'liver', order: 6 },
  'total_bilirubin': { label: 'Total Bilirubin', unit: 'mg/dL', category: 'liver', order: 7 },
  'direct_bilirubin': { label: 'Direct Bilirubin', unit: 'mg/dL', category: 'liver', order: 8 },
  'ggt': { label: 'GGT', unit: 'U/L', category: 'liver', order: 9 },

  // ===== กรดยูริค =====
  'uric_acid': { label: 'Uric Acid', unit: 'mg/dL', category: 'uric', order: 1 },
  'uric': { label: 'Uric Acid', unit: 'mg/dL', category: 'uric', order: 1 },

  // ===== ผลการตรวจปัสสาวะ =====
  // Physical
  'color': { label: 'สีของปัสสาวะ', unit: '', category: 'urine', order: 1 },
  'urine_color': { label: 'สีของปัสสาวะ', unit: '', category: 'urine', order: 1 },
  'urinalysis_color': { label: 'สีของปัสสาวะ', unit: '', category: 'urine', order: 1 },
  'clarity': { label: 'ความใส (Clarity)', unit: '', category: 'urine', order: 2 },
  'urine_clarity': { label: 'ความใส (Clarity)', unit: '', category: 'urine', order: 2 },
  'urine_appearance': { label: 'ลักษณะปัสสาวะ (Appearance)', unit: '', category: 'urine', order: 3 },
  'urinalysis_appearance': { label: 'ลักษณะปัสสาวะ (Appearance)', unit: '', category: 'urine', order: 3 },
  'appearance': { label: 'ลักษณะปัสสาวะ (Appearance)', unit: '', category: 'urine', order: 3 },
  // Chemical
  'ph': { label: 'ค่า pH', unit: '', category: 'urine', order: 4 },
  'urine_ph': { label: 'ค่า pH', unit: '', category: 'urine', order: 4 },
  'urinalysis_ph': { label: 'ค่า pH', unit: '', category: 'urine', order: 4 },
  'specificgravity': { label: 'ความถ่วงจำเพาะ', unit: '', category: 'urine', order: 5 },
  'specific_gravity': { label: 'ความถ่วงจำเพาะ', unit: '', category: 'urine', order: 5 },
  'urinalysis_specific_gravity': { label: 'ความถ่วงจำเพาะ', unit: '', category: 'urine', order: 5 },
  'protein': { label: 'โปรตีน (Protein)', unit: '', category: 'urine', order: 6 },
  'urine_protein': { label: 'โปรตีน (Protein)', unit: '', category: 'urine', order: 6 },
  'urinalysis_protein': { label: 'โปรตีน (Protein)', unit: '', category: 'urine', order: 6 },
  'urine_glucose': { label: 'น้ำตาล (Glucose)', unit: '', category: 'urine', order: 7 },
  'urinalysis_glucose': { label: 'น้ำตาล (Glucose)', unit: '', category: 'urine', order: 7 },
  'ketones': { label: 'คีโตน (Ketone)', unit: '', category: 'urine', order: 8 },
  'urine_ketone': { label: 'คีโตน (Ketone)', unit: '', category: 'urine', order: 8 },
  'urinalysis_ketone': { label: 'คีโตน (Ketone)', unit: '', category: 'urine', order: 8 },
  'blood': { label: 'เลือด (Blood)', unit: '', category: 'urine', order: 9 },
  'urine_blood': { label: 'เลือด (Blood)', unit: '', category: 'urine', order: 9 },
  'urinalysis_blood': { label: 'เลือด (Blood)', unit: '', category: 'urine', order: 9 },
  'leukocytes': { label: 'Leukocyte', unit: '', category: 'urine', order: 10 },
  'urine_leukocyte': { label: 'Leukocyte', unit: '', category: 'urine', order: 10 },
  'urine_leukocytes': { label: 'Leukocyte', unit: '', category: 'urine', order: 10 },
  'urinalysis_leukocyte': { label: 'Leukocyte', unit: '', category: 'urine', order: 10 },
  'nitrite': { label: 'Nitrite', unit: '', category: 'urine', order: 11 },
  'urine_nitrite': { label: 'Nitrite', unit: '', category: 'urine', order: 11 },
  'urinalysis_nitrite': { label: 'Nitrite', unit: '', category: 'urine', order: 11 },
  'bilirubin': { label: 'Bilirubin', unit: '', category: 'urine', order: 12 },
  'urine_bilirubin': { label: 'Bilirubin', unit: '', category: 'urine', order: 12 },
  'urinalysis_bilirubin': { label: 'Bilirubin', unit: '', category: 'urine', order: 12 },
  'urobilinogen': { label: 'Urobilinogen', unit: '', category: 'urine', order: 13 },
  'urine_urobilinogen': { label: 'Urobilinogen', unit: '', category: 'urine', order: 13 },
  'urinalysis_urobilinogen': { label: 'Urobilinogen', unit: '', category: 'urine', order: 13 },
  // Microscopic
  'redbloodcells': { label: 'เซลล์เม็ดเลือดแดง', unit: 'cells/HPF', category: 'urine', order: 14 },
  'urine_rbc': { label: 'เซลล์เม็ดเลือดแดง', unit: 'cells/HPF', category: 'urine', order: 14 },
  'urinalysis_rbc': { label: 'เซลล์เม็ดเลือดแดง', unit: 'cells/HPF', category: 'urine', order: 14 },
  'whitebloodcells': { label: 'เซลล์เม็ดเลือดขาว', unit: 'cells/HPF', category: 'urine', order: 15 },
  'urine_wbc': { label: 'เซลล์เม็ดเลือดขาว', unit: 'cells/HPF', category: 'urine', order: 15 },
  'urinalysis_wbc': { label: 'เซลล์เม็ดเลือดขาว', unit: 'cells/HPF', category: 'urine', order: 15 },
  'bacteria': { label: 'แบคทีเรีย', unit: '', category: 'urine', order: 16 },
  'urine_bacteria': { label: 'แบคทีเรีย', unit: '', category: 'urine', order: 16 },
  'urinalysis_bacteria': { label: 'แบคทีเรีย', unit: '', category: 'urine', order: 16 },
  'epithelial': { label: 'เซลล์เยื่อผิว (Epithelial Cells)', unit: '', category: 'urine', order: 17 },
  'epithelial_cells': { label: 'เซลล์เยื่อผิว (Epithelial Cells)', unit: '', category: 'urine', order: 17 },
  'epithelialcells': { label: 'เซลล์เยื่อผิว (Epithelial Cells)', unit: '', category: 'urine', order: 17 },
  'urinalysis_epithelial': { label: 'เซลล์เยื่อผิว (Epithelial Cells)', unit: '', category: 'urine', order: 17 },
  // Cast types (อยู่ใน "ผลการตรวจปัสสาวะ" ตาม medium.json)
  'cast': { label: 'Cast', unit: '', category: 'urine', order: 20 },
  'hyaline_cast': { label: 'Hyaline cast', unit: '', category: 'urine', order: 21 },
  'hyalinecast': { label: 'Hyaline cast', unit: '', category: 'urine', order: 21 },
  'granular_cast': { label: 'Granular cast', unit: '', category: 'urine', order: 22 },
  'granularcast': { label: 'Granular cast', unit: '', category: 'urine', order: 22 },
  'cellular_cast': { label: 'Cellular cast', unit: '', category: 'urine', order: 23 },
  'cellularcast': { label: 'Cellular cast', unit: '', category: 'urine', order: 23 },
  'waxy_cast': { label: 'Waxy cast', unit: '', category: 'urine', order: 24 },
  'waxycast': { label: 'Waxy cast', unit: '', category: 'urine', order: 24 },
  // Crystal types (อยู่ใน "ผลการตรวจปัสสาวะ" ตาม medium.json)
  'crystal': { label: 'Crystal', unit: '', category: 'urine', order: 25 },
  'calcium_oxalate': { label: 'Calcium oxalate crystal', unit: '', category: 'urine', order: 26 },
  'calciumoxalate': { label: 'Calcium oxalate crystal', unit: '', category: 'urine', order: 26 },
  'calcium_carbonate': { label: 'Calcium Carbonate crystal', unit: '', category: 'urine', order: 27 },
  'calciumcarbonate': { label: 'Calcium Carbonate crystal', unit: '', category: 'urine', order: 27 },
  'uric_acid_crystal': { label: 'Uric acid crystal', unit: '', category: 'urine', order: 28 },
  'uricacidcrystal': { label: 'Uric acid crystal', unit: '', category: 'urine', order: 28 },
  'triple_phosphate': { label: 'Triple phosphate crystal', unit: '', category: 'urine', order: 29 },
  'triplephosphate': { label: 'Triple phosphate crystal', unit: '', category: 'urine', order: 29 },
  // Other
  'yeast': { label: 'Yeast', unit: '', category: 'urine', order: 30 },
  'mucus': { label: 'Mucus', unit: '', category: 'urine', order: 31 },

  // ===== ผลการตรวจอุจจาระ =====
  'stoolcolor': { label: 'สีของอุจจาระ', unit: '', category: 'stool', order: 1 },
  'stool_color': { label: 'สีของอุจจาระ', unit: '', category: 'stool', order: 1 },
  'stool_color_result': { label: 'สีของอุจจาระ', unit: '', category: 'stool', order: 1 },
  'stoolconsistency': { label: 'ลักษณะอุจจาระ', unit: '', category: 'stool', order: 2 },
  'stool_consistency': { label: 'ลักษณะอุจจาระ', unit: '', category: 'stool', order: 2 },
  'stool_consistency_result': { label: 'ลักษณะอุจจาระ', unit: '', category: 'stool', order: 2 },
  'stoolwbc': { label: 'เม็ดเลือดขาว (WBC)', unit: '', category: 'stool', order: 3 },
  'stool_wbc': { label: 'เม็ดเลือดขาว (WBC)', unit: '', category: 'stool', order: 3 },
  'stool_wbc_result': { label: 'เม็ดเลือดขาว (WBC)', unit: '', category: 'stool', order: 3 },
  'stoolrbc': { label: 'เม็ดเลือดแดง (RBC)', unit: '', category: 'stool', order: 4 },
  'stool_rbc': { label: 'เม็ดเลือดแดง (RBC)', unit: '', category: 'stool', order: 4 },
  'stool_rbc_result': { label: 'เม็ดเลือดแดง (RBC)', unit: '', category: 'stool', order: 4 },
  'stoolparasite': { label: 'พยาธิ, ไข่พยาธิ', unit: '', category: 'stool', order: 5 },
  'stool_parasite': { label: 'พยาธิ, ไข่พยาธิ', unit: '', category: 'stool', order: 5 },
  'stool_parasite_result': { label: 'พยาธิ, ไข่พยาธิ', unit: '', category: 'stool', order: 5 },
  'parasite': { label: 'พยาธิ, ไข่พยาธิ', unit: '', category: 'stool', order: 5 },
  'stooloccultblood': { label: 'Hemoglobin Detection Stool', unit: '', category: 'stool_occult', order: 1 },
  'stool_occult_blood': { label: 'Hemoglobin Detection Stool', unit: '', category: 'stool_occult', order: 1 },
  'stool_occult_blood_result': { label: 'Hemoglobin Detection Stool', unit: '', category: 'stool_occult', order: 1 },
  'occult_blood': { label: 'Hemoglobin Detection Stool', unit: '', category: 'stool_occult', order: 1 },
  'hemoglobin_detection_stool': { label: 'Hemoglobin Detection Stool', unit: '', category: 'stool_occult', order: 1 },

  // ===== เกลือแร่ในเลือด =====
  'sodium': { label: 'Sodium', unit: 'mmol/L', category: 'electrolyte', order: 1 },
  'potassium': { label: 'Potassium', unit: 'mmol/L', category: 'electrolyte', order: 2 },
  'chloride': { label: 'Chloride', unit: 'mmol/L', category: 'electrolyte', order: 3 },
  'bicarbonate': { label: 'Bicarbonate', unit: 'mmol/L', category: 'electrolyte', order: 4 },
  'total_calcium': { label: 'Total Calcium', unit: 'mg/dL', category: 'electrolyte', order: 5 },
  'calcium': { label: 'Total Calcium', unit: 'mg/dL', category: 'electrolyte', order: 5 },
  'inorganic_phosphate': { label: 'Inorganic Phosphate', unit: 'mg/dL', category: 'electrolyte', order: 6 },
  'phosphate': { label: 'Inorganic Phosphate', unit: 'mg/dL', category: 'electrolyte', order: 6 },
  'magnesium': { label: 'Magnesium', unit: 'mg/dL', category: 'electrolyte', order: 7 },

  // ===== ไวรัสตับอักเสบ =====
  'hbsag': { label: 'HBsAg', unit: '', category: 'hepatitis', order: 1 },
  'hbsag_concentration': { label: 'HBsAg Concentration', unit: 'IU/mL', category: 'hepatitis', order: 2 },
  'hbsab': { label: 'HBsAb', unit: '', category: 'hepatitis', order: 3 },
  'anti_hbs': { label: 'Anti-HBs Concentration', unit: 'IU/L', category: 'hepatitis', order: 4 },
  'antihbs': { label: 'Anti-HBs Concentration', unit: 'IU/L', category: 'hepatitis', order: 4 },
  'hcv_ab': { label: 'HCV Ab', unit: '', category: 'hepatitis', order: 5 },
  'hcvab': { label: 'HCV Ab', unit: '', category: 'hepatitis', order: 5 },
  'coi': { label: 'COI', unit: '', category: 'hepatitis', order: 6 },

  // ===== ค่าบ่งชี้มะเร็ง =====
  'afp': { label: 'AFP', unit: 'ng/ml', category: 'tumor', order: 1 },
  'psa': { label: 'PSA', unit: 'ng/ml', category: 'tumor', order: 2 },
  'cea': { label: 'CEA', unit: 'ng/mL', category: 'tumor', order: 3 },
  'ca19_9': { label: 'CA19-9', unit: 'U/mL', category: 'tumor', order: 4 },
  'ca19-9': { label: 'CA19-9', unit: 'U/mL', category: 'tumor', order: 4 },
  'ca125': { label: 'CA125', unit: 'U/mL', category: 'tumor', order: 5 },

  // ===== ฮอร์โมนไทรอยด์ =====
  'tsh': { label: 'TSH', unit: 'uIU/mL', category: 'thyroid', order: 1 },
  'free_t3': { label: 'Free T3', unit: 'pg/mL', category: 'thyroid', order: 2 },
  'ft3': { label: 'Free T3', unit: 'pg/mL', category: 'thyroid', order: 2 },
  'free_t4': { label: 'Free T4', unit: 'ng/dL', category: 'thyroid', order: 3 },
  'ft4': { label: 'Free T4', unit: 'ng/dL', category: 'thyroid', order: 3 },
  't3': { label: 'T3', unit: 'ng/dL', category: 'thyroid', order: 4 },
  't4': { label: 'T4', unit: 'µg/dL', category: 'thyroid', order: 5 },

  // ===== การสัมผัสสารพิษกลุ่มยาฆ่าแมลง =====
  'cholinesterase': { label: 'Cholinesterase', unit: 'U/L', category: 'pesticide', order: 1 },
};

// Parameters ที่มี _hide_ prefix ใน medium.json → ซ่อนเมื่อค่าเป็น "Negative"
// (เหมือน logic ของ admin healthbook.js และ HealthReportModal.js)
const HIDE_WHEN_NEGATIVE = new Set([
  // Urine casts
  'hyaline_cast', 'granular_cast', 'cellular_cast', 'waxy_cast',
  // Urine crystals
  'calcium_oxalate', 'calcium_carbonate', 'uric_acid_crystal', 'triple_phosphate',
]);

// Category labels (ชื่อหมวดหมู่ตรงกับ medium.json)
const CATEGORY_LABELS = {
  vital: 'รายการตรวจ',                               // vital signs (ไม่อยู่ใน medium.json)
  kidney: 'การทำงานของไต',
  sugar: 'น้ำตาลในเลือด',
  lipid: 'ไขมันในเลือด',
  liver: 'การทำงานของตับ',
  cbc: 'ความสมบูรณ์ของเลือด',
  urine: 'ผลการตรวจปัสสาวะ',
  uric: 'กรดยูริคในเลือด',
  stool: 'ผลการตรวจอุจจาระ',
  stool_occult: 'การตรวจเลือดแฝงในอุจจาระ',
  electrolyte: 'เกลือแร่ในเลือด',
  hepatitis: 'ไวรัสตับอักเสบ',
  tumor: 'ค่าบ่งชี้มะเร็ง',
  thyroid: 'ฮอร์โมนไทรอยด์',
  pesticide: 'การสัมผัสสารพิษกลุ่มยาฆ่าแมลง',
};

// Category order (ลำดับตรงกับ medium.json)
const CATEGORY_ORDER = [
  'vital',          // รายการตรวจ (vital signs)
  'kidney',         // การทำงานของไต
  'sugar',          // น้ำตาลในเลือด
  'lipid',          // ไขมันในเลือด
  'liver',          // การทำงานของตับ
  'cbc',            // ความสมบูรณ์ของเลือด
  'urine',          // ผลการตรวจปัสสาวะ (รวม casts & crystals)
  'uric',           // กรดยูริคในเลือด
  'stool',          // ผลการตรวจอุจจาระ
  'stool_occult',   // การตรวจเลือดแฝงในอุจจาระ
  'electrolyte',    // เกลือแร่ในเลือด
  'hepatitis',      // ไวรัสตับอักเสบ
  'tumor',          // ค่าบ่งชี้มะเร็ง
  'thyroid',        // ฮอร์โมนไทรอยด์
  'pesticide',      // การสัมผัสสารพิษกลุ่มยาฆ่าแมลง
];

const TestResultsPage = () => {
  const [loading, setLoading] = useState(true);
  const [groupedReports, setGroupedReports] = useState([]);
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  // map จาก visit date prefix → history_id (สำหรับ download PDF)
  const [historyIdMap, setHistoryIdMap] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // ====== ลองดึงข้อมูลจาก MFU API ก่อน (เส้นเดียวกับ mfu-admin) ======
        let labData = [];

        try {
          const memberLabData = await getMemberLabData();

          if (memberLabData?.labData) {
            // แปลงข้อมูลจาก memberLabData ให้อยู่ในรูปแบบที่ groupDataByDate ใช้
            const formattedData = [];
            const allLabData = memberLabData.labData;

            Object.keys(allLabData).forEach(paramName => {
              const paramHistory = allLabData[paramName];
              if (Array.isArray(paramHistory)) {
                paramHistory.forEach(item => {
                  formattedData.push({
                    dateadd: item.date,
                    name: paramName,
                    value: item.value
                  });
                });
              }
            });

            if (formattedData.length > 0) {
              labData = formattedData;
            }
          }
        } catch (memberLabError) {
        }

        // Fallback ไป API เดิมถ้าไม่มีข้อมูล
        if (labData.length === 0) {
          const labResponse = await getLabData('', 1);
          labData = labResponse?.data || labResponse?.result || labResponse?.list || [];
        }

        // Group data by date
        const grouped = groupDataByDate(labData);
        setGroupedReports(grouped);

        // โหลด notifications เพื่อ map visit_date → history_id สำหรับ download PDF
        try {
          const notiResponse = await getNotifications();
          if (notiResponse?.status === 'success' && Array.isArray(notiResponse?.data?.notifications)) {
            const map = {};
            // เรียงจากเก่าสุด → ใหม่สุด เพื่อให้อันล่าสุดทับอันเก่าเสมอ
            const sorted = [...notiResponse.data.notifications].reverse();
            sorted.forEach(n => {
              const datetimeKey = (n.checkup_date || n.sent_at || '').trim();
              if (datetimeKey && n.id) map[datetimeKey] = n.id;
            });
            setHistoryIdMap(map);
          }
        } catch (_) {}

      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const groupDataByDate = (data) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    // Group by date-only (รวม record จากวันเดียวกันเข้าด้วยกัน)
    const groupedByDateTime = {};

    data.forEach(item => {
      let datetime = item.dateadd || '';
      if (!datetime) return;

      // Normalize datetime: ลบ timezone และ normalize format
      // เช่น "2026-01-27T14:31:04.000Z" -> "2026-01-27 14:31:04"
      datetime = datetime.replace('T', ' ').split('.')[0].split('Z')[0].trim();

      // ใช้เฉพาะวันที่เป็น key (รวม record ต่าง timestamp ของวันเดียวกัน)
      const date = datetime.split(' ')[0];
      if (!groupedByDateTime[date]) {
        groupedByDateTime[date] = {
          date: date,
          datetime: datetime,
          dateDisplay: formatDate(date),
          location: 'MFU Wellness Center',
          measurements: {}
        };
      }

      // Store each measurement with its name as key (lowercase for matching)
      const name = item.name?.toLowerCase()?.trim();
      const value = item.value;

      if (name && value !== undefined && value !== null && value !== '') {
        if (name === 'hospital') {
          groupedByDateTime[date].location = value;
        } else {
          const existing = groupedByDateTime[date].measurements[name];
          const isRange = /^[0-9]{2,}-[0-9]{2,}$/.test(value) || /^[0-9]+\.[0-9]+-[0-9]+\.[0-9]+$/.test(value);
          // ถ้ายังไม่มีค่า หรือค่าที่มีอยู่ดูเหมือน range ให้เขียนทับ
          if (!existing || /^[0-9]{2,}-[0-9]{2,}$/.test(existing) || /^[0-9]+\.[0-9]+-[0-9]+\.[0-9]+$/.test(existing)) {
            if (!isRange || !existing) {
              groupedByDateTime[date].measurements[name] = value;
            }
          }
        }
      }
    });

    // Convert to array and sort by date (newest first)
    return Object.values(groupedByDateTime).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';

    // แยกเฉพาะส่วนวันที่ ถ้ามี timestamp
    const datePart = dateString.split('T')[0].split(' ')[0];

    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return dateString; // ถ้า format ไม่ถูกต้องให้ return กลับไป

    const thaiYear = parseInt(year) + 543;
    const shortYear = thaiYear.toString().slice(-2);
    return `${day}/${month}/${shortYear}`;
  };

  const handleDownload = async (reportDate, rawDate) => {
    try {

      // ดึง NID จาก localStorage (username)
      const nid = localStorage.getItem('username');

      if (!nid) {
        alert('ไม่พบข้อมูล NID กรุณาเข้าสู่ระบบใหม่');
        return;
      }

      // หา history_id จาก datetime ของ report นี้ (ถ้า match ได้)
      const historyId = historyIdMap[(rawDate || '').trim()] || null;

      // ส่ง history_id ถ้า match ได้ ถ้าไม่ได้ส่ง visit_date ให้ backend หาเอง
      const response = await createShareTokenMFUUser(nid, historyId, 24, historyId ? null : rawDate);

      if (response?.status === 'success' && response?.data?.share_url) {
        const shareUrl = response.data.share_url;

        // เปิด PDF ใน modal
        setPdfUrl(shareUrl);
        setIsPDFViewerOpen(true);
      } else {
        throw new Error('ไม่ได้รับ share URL จาก server');
      }
    } catch (err) {
      alert(
        'เกิดข้อผิดพลาดในการเปิด PDF\n' +
          (err.message || 'กรุณาลองใหม่อีกครั้ง')
      );
    }
  };

  const handleClosePDFViewer = () => {
    setIsPDFViewerOpen(false);
    setPdfUrl(null);
  };

  const handleTabClick = (tab) => {
    if (tab === 'สุขภาพของคุณ') {
      window.location.href = '/dashboard';
    } else if (tab === 'ผลตรวจเปรียบเทียบ') {
      window.location.href = '/comparison-results';
    }
  };

  // Group measurements by category
  const groupMeasurementsByCategory = (measurements) => {
    const grouped = {};

    Object.entries(measurements).forEach(([name, value]) => {
      // ลอง match ทั้ง lowercase และ original
      const fieldInfo = FIELD_MAPPING[name] || FIELD_MAPPING[name.toLowerCase()];

      if (fieldInfo) {
        // ซ่อน _hide_ parameters เมื่อค่าเป็น "Negative" (เหมือน admin)
        const valueLower = (value || '').toString().toLowerCase().trim();
        if (HIDE_WHEN_NEGATIVE.has(name) && (valueLower === 'negative' || valueLower === 'neg' || valueLower === '-' || valueLower === '')) {
          return;
        }

        const category = fieldInfo.category;
        if (!grouped[category]) {
          grouped[category] = [];
        }

        // ตรวจสอบว่ายังไม่มี item ที่มี label เดียวกัน (ป้องกัน duplicate)
        const existingItem = grouped[category].find(item => item.label === fieldInfo.label);
        if (!existingItem) {
          grouped[category].push({
            name,
            label: fieldInfo.label,
            value,
            unit: fieldInfo.unit,
            order: fieldInfo.order || 999
          });
        }
      } else {
        // ไม่แสดง field ที่ไม่รู้จัก (admin ก็ไม่แสดง)
      }
    });

    // Sort items in each category by order
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => a.order - b.order);
    });

    return grouped;
  };

  // Format value with unit
  const formatValue = (value, unit) => {
    if (value === undefined || value === null || value === '') return '-';
    if (!unit) return String(value);
    // ไม่ต่อหน่วยถ้าค่ามีหน่วยอยู่แล้ว เช่น "0-1Cells/HPF"
    if (String(value).toLowerCase().includes(unit.toLowerCase())) return String(value);
    return `${value} ${unit}`;
  };

  // Render category section - แสดงเฉพาะเมื่อมีข้อมูล
  const renderCategorySection = (category, items) => {
    if (!items || items.length === 0) return null;

    return (
      <div key={category}>
        <SectionTitle>{CATEGORY_LABELS[category] || category}</SectionTitle>
        {items.map((item, idx) => (
          <InfoRow key={idx}>
            <InfoLabel indent={true}>{item.label}</InfoLabel>
            <InfoValue>{formatValue(item.value, item.unit)}</InfoValue>
          </InfoRow>
        ))}
      </div>
    );
  };

  return (
    <TestResultsContainer>
      <MainHeader />

      <TabContainer>
        <Tab onClick={() => handleTabClick('สุขภาพของคุณ')}>สุขภาพของคุณ</Tab>
        <Tab active>ผลตรวจรวม</Tab>
        <Tab onClick={() => handleTabClick('ผลตรวจเปรียบเทียบ')}>ผลตรวจเปรียบเทียบ</Tab>
      </TabContainer>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : groupedReports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            ไม่พบข้อมูลผลตรวจ
          </div>
        ) : (
          groupedReports.map((report, index) => {
            const groupedMeasurements = groupMeasurementsByCategory(report.measurements);

            // ตรวจสอบว่ามีข้อมูลอย่างน้อย 1 category
            const hasAnyData = CATEGORY_ORDER.some(cat =>
              groupedMeasurements[cat] && groupedMeasurements[cat].length > 0
            );

            if (!hasAnyData) return null;

            return (
              <CardContainer key={index}>
                <DownloadContainer>
                  <DownloadButton onClick={() => handleDownload(report.dateDisplay, report.datetime)}>
                    <span>Download</span>
                    <PDFIcon src="./PDF.png" alt="PDF" />
                  </DownloadButton>
                </DownloadContainer>

                <ResultCard>
                  <CardHeader>
                    <HeaderText>ผลตรวจ</HeaderText>
                  </CardHeader>

                  <CardContent>
                    <InfoRow>
                      <InfoLabel bold>วันที่ตรวจ</InfoLabel>
                      <InfoValue>{report.dateDisplay}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel bold>ตรวจที่</InfoLabel>
                      <InfoValue>{report.location}</InfoValue>
                    </InfoRow>

                    <Divider />

                    {/* Render all categories in order - แสดงเฉพาะที่มีข้อมูล */}
                    {CATEGORY_ORDER.map(category =>
                      renderCategorySection(category, groupedMeasurements[category])
                    )}
                  </CardContent>
                </ResultCard>
              </CardContainer>
            );
          })
        )}
      </Content>

      <PDFViewerModal
        isOpen={isPDFViewerOpen}
        onClose={handleClosePDFViewer}
        pdfUrl={pdfUrl}
        title="ผลการตรวจสุขภาพ"
      />
    </TestResultsContainer>
  );
};

export default TestResultsPage;
